import { useState, useCallback } from 'react';
import { useSelector, useDispatch, RootStateOrAny } from 'react-redux';
import * as actions from '~/store/actions';
import { MessageTypeEnum, ChannelTypeEnum } from '~/enums/conversations';
import { DraftMessage } from '~/interfaces/conversations';
import { _ } from '~/dependencies';
import { log, electron } from '~/helpers';
import api from '~/services';

export enum SMSErrorCode {
  SMS_INSUFFICIENT_CREDIT = 6001,
  SMS_NO_VALID_RECIPIENTS = 6002,
  SMS_INVALID_SENDER_ID = 6003,
  SMS_IRISH_COMREG_RESTRICTED = 6004,
  SMS_USA_CAMPAIGN_NOT_APPROVED = 6005,
  SMS_DELIVERY_FAILED = 6006,
  SMS_SCHEDULED_MESSAGE_ERROR = 6007,
  SMS_BULK_SEND_PARTIAL_FAILURE = 6008,
  SMS_BULK_SEND_COMPLETE_FAILURE = 6009,
  SMS_DRAFT_MESSAGE_NOT_FOUND = 6010,
  SMS_INSUFFICIENT_LIMIT = 6011,
  SMS_ERROR = 400,
}

export enum LaravelErrorCodeEnum {
  INVALID_DATA = 42222,
}

export enum ErrorCodeEnum {
  UNKNOWN = 60000,
}

/**
 * The API is retunning the errors in different formats. This function normalizes
 * the error object to a standard structure.
 */
export function getErrorCode(error: any) {
  if (typeof error !== 'object') return ErrorCodeEnum.UNKNOWN;

  if ('data' in error) {
    if ('errorCode' in error.data) {
      return error.errorCode;
    }

    // Laravel validation errors
    if ('errors' in error.data) {
      return LaravelErrorCodeEnum.INVALID_DATA;
    }
  }

  return ErrorCodeEnum.UNKNOWN;
}

/**
 * Classifies the message type based on the presence of a template ID or the file type.
 * Defaults to TEXT if no specific type is identified.
 */
export function classifyMessageType({
  templateId,
  file,
}: DraftMessage): MessageTypeEnum {
  if (templateId) return MessageTypeEnum.TEXT;

  if (file) {
    if (file.type.startsWith('image/')) return MessageTypeEnum.IMAGE;
    if (file.type.startsWith('video/')) return MessageTypeEnum.VIDEO;
    if (file.type.startsWith('audio/')) return MessageTypeEnum.AUDIO;
    return MessageTypeEnum.FILE;
  }

  return MessageTypeEnum.TEXT;
}

export function isFileMessageType(messageType: MessageTypeEnum): boolean {
  const messageTypes = [
    MessageTypeEnum.IMAGE,
    MessageTypeEnum.VIDEO,
    MessageTypeEnum.AUDIO,
    MessageTypeEnum.FILE,
  ];

  return messageTypes.includes(messageType);
}

type CreateConversationParams = {
  channelType: number;
  /**
   * The list of phone number to receive the message e.g, "+3530000000,+35311111111"
   */
  contacts: string;
  /**
   * The list of group IDs to receive the message e.g, "1,2,3"
   */
  groups: string;
  senderId: string | undefined;
  senderName: string | undefined;
  templateId: number | undefined;
  message: string;
  templateParams?: any;
};

type NewConversationBasePayload = {
  channelType: number;
  contacts: string;
  senderId: string | undefined;
  templateId: number | undefined;
  userId: number;
  userName: string;
};

type SMSExtraPayload = {
  groups: string;
  senderName: string | undefined;
  message: string;
  accountId: number;
};

type WhatsAppExtraPayload = {
  templateParams?: any;
  groups?: string;
};

function createWhatsAppWithTemplatePayload({
  channelType,
  contacts,
  senderId,
  templateId,
  templateParams,
  userId,
  userName,
  groups,
}: NewConversationBasePayload & WhatsAppExtraPayload) {
  const templateVariables = templateParams || {};

  const senderParticipant = {
    participant_type: 'user',
    participant_id: userId,
    display_name: userName,
    is_system_user: 1,
  };

  const contactParticipants = contacts
    .split(',')
    .map(contact => contact.trim())
    .filter(contact => !_.isEmpty(contact))
    .map(contact => ({
      participant_type: 'phone_number',
      participant_id: contact,
      display_name: contact,
    }));

  return {
    channel_type: channelType,
    sender_id: senderId,
    participants: [senderParticipant, ...contactParticipants],
    ...(_.isString(groups) && !_.isEmpty(groups) && { groups }),
    message: {
      message_type: MessageTypeEnum.TEXT,
      metadata: {
        ...(_.isNumber(templateId) && { template_id: templateId }),
        ...(!_.isEmpty(templateVariables) && {
          template_variables: templateVariables,
        }),
      },
    },
  };
}

function createSMSPayload({
  channelType,
  contacts,
  groups,
  senderId,
  senderName,
  templateId,
  message,
  userId,
  accountId,
}: NewConversationBasePayload & SMSExtraPayload) {
  return {
    channel_type: channelType,
    title: senderName || null,
    sender_id: senderId || null,
    is_group: false,
    owner_id: userId,
    account_id: accountId,
    ...(_.isString(contacts) && !_.isEmpty(contacts) && { contacts }),
    ...(_.isString(groups) && !_.isEmpty(groups) && { groups }),
    message: {
      content: message,
      message_type: 0,
      ...(_.isNumber(templateId) && { metadata: { template_id: templateId } }),
    },
  };
}

/**
 * Maps SMS error codes to user-friendly messages.
 */
export function getErrorMessageByCode(
  errorCode: number = ErrorCodeEnum.UNKNOWN
): string {
  switch (errorCode) {
    case SMSErrorCode.SMS_INSUFFICIENT_CREDIT:
      return "You don't have enough credit to send this message.";
    case SMSErrorCode.SMS_NO_VALID_RECIPIENTS:
      return 'No valid recipients were found for this message.';
    case SMSErrorCode.SMS_INVALID_SENDER_ID:
      return 'The sender ID is invalid. Please verify and try again.';
    case SMSErrorCode.SMS_IRISH_COMREG_RESTRICTED:
      return 'Message sending is restricted by Irish ComReg regulations.';
    case SMSErrorCode.SMS_USA_CAMPAIGN_NOT_APPROVED:
      return 'The SMS campaign has not been approved for the USA region.';
    case SMSErrorCode.SMS_DELIVERY_FAILED:
      return 'Message delivery failed. Please try again later.';
    case SMSErrorCode.SMS_SCHEDULED_MESSAGE_ERROR:
      return 'There was an issue with the scheduled message.';
    case SMSErrorCode.SMS_BULK_SEND_PARTIAL_FAILURE:
      return 'Some messages failed to send in this bulk operation.';
    case SMSErrorCode.SMS_BULK_SEND_COMPLETE_FAILURE:
      return 'All messages failed to send. Please review your recipients.';
    case SMSErrorCode.SMS_DRAFT_MESSAGE_NOT_FOUND:
      return 'The draft message could not be found.';
    case SMSErrorCode.SMS_INSUFFICIENT_LIMIT:
      return "You've reached your SMS limit. Please upgrade or wait for renewal.";
    case SMSErrorCode.SMS_ERROR:
      return 'No valid numbers to send to.';
    case LaravelErrorCodeEnum.INVALID_DATA:
      return 'The given data was invalid.';
    default:
      return "We're sorry, something went wrong. Please try again shortly.";
  }
}

export default function useConversation() {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<boolean>(false);

  const {
    account_id: accountId,
    userIdCode: userId,
    user_name: userName,
  } = useSelector((state: RootStateOrAny) => state.auth);

  /**
   * Create a new conversation and returns conversationID
   */
  const create = useCallback(
    async ({
      channelType,
      contacts,
      groups,
      senderId,
      senderName,
      templateId,
      message,
      templateParams,
    }: CreateConversationParams) => {
      try {
        if (!accountId || !userId) {
          throw new Error('User authentication details are missing.');
        }

        setLoading(true);

        let payload: any = {};

        if (channelType === ChannelTypeEnum.WHATSAPP) {
          // WhatsApp has a distinct payload structure. We need to use the `participants` array instead of `contacts`.
          payload = createWhatsAppWithTemplatePayload({
            channelType,
            contacts,
            senderId,
            templateId,
            templateParams,
            userId,
            userName,
            groups,
          });
        } else {
          // Used for SMS only, as whatspp can only start with templates.
          payload = createSMSPayload({
            channelType,
            contacts,
            groups,
            senderId,
            senderName,
            templateId,
            message,
            accountId,
            userId,
            userName,
          });
        }

        const response = await api.conversation.post.createConversation(
          payload
        );

        if (response.data?.id) {
          setConversationId(response.data.id);

          // Refresh conversation details to reflect the new message after 10 seconds.
          setTimeout(() => {
            dispatch(actions.fetchConversationById(response.data?.id, false));
          }, 10000); // 10 seconds
        }
      } catch (err) {
        const errorCode = getErrorMessageByCode(getErrorCode(err));
        setErrorMessage(errorCode);

        // Dont now log message or contacts which contains personal info to main.log, but log them to web log for debugging.
        const errorNewMessageFormatted = `NUACOM-LOG: [CONVERSATIONS] - Failed to start new conversation. Reason: ${errorCode} | Raw Error: ${JSON.stringify(
          err
        )} | Payload: ${JSON.stringify({
          channelType,
          groups,
          senderId,
          senderName,
          templateId,
          templateParams,
        })}`;

        log.error(errorNewMessageFormatted, {
          message,
          contacts,
        });
        electron.log(errorNewMessageFormatted);
      } finally {
        setLoading(false);
        setSubmitted(true);
      }
    },
    [
      setLoading,
      setSubmitted,
      setConversationId,
      accountId,
      userId,
      dispatch,
      userName,
    ]
  );

  /**
   * Add message to existing conversation using conversationID
   */
  const addMessage = useCallback(
    async (draft: DraftMessage) => {
      const targetId = draft.conversationId!;
      const content = draft?.text;
      const contentFile = draft?.file;
      const templateId = draft?.templateId;
      const templateVariables = draft?.templateParams;
      const channelType = draft?.channelType;

      try {
        if (!targetId || !channelType) {
          throw new Error('Conversation ID or Channel Type is missing.');
        }

        setLoading(true);

        const messageType = classifyMessageType(draft);

        // Use FormData when there's a file to upload
        let payload: any;

        // Maybe need change when MMS comes
        if (isFileMessageType(messageType)) {
          payload = new FormData();
          payload.append('channel_type', channelType);
          payload.append('message_type', messageType);
          payload.append('content_file', contentFile);
        } else {
          // Use regular object when there's no files
          const metadata = {
            ...(_.isNumber(templateId) && { template_id: templateId }),
            ...(!_.isEmpty(templateVariables) && {
              template_variables: templateVariables,
            }),
          };

          payload = {
            content,
            message_type: messageType,
            channel_type: channelType,
            ...(!_.isEmpty(metadata) && { metadata }),
          };
        }

        await api.conversation.post.addMessageToConversation(targetId, payload);

        // Immediately refresh conversation details to reflect the new message.
        setTimeout(() => {
          dispatch(actions.fetchConversationById(targetId, false));
        }, 1000); // 1 second

        // Retry to refresh conversation details to reflect the new message after 10 seconds.
        setTimeout(() => {
          dispatch(actions.fetchConversationById(targetId, false));
        }, 10000); // 10 seconds
      } catch (err) {
        const errorCodeAddingMessage =
          (err as any)?.isAxiosError && (err as any)?.response?.data?.message
            ? (err as any).response?.data?.message
            : getErrorMessageByCode(getErrorCode(err));

        setErrorMessage(errorCodeAddingMessage);

        // Dont now log message or contacts which contains personal info to main.log, but log them to web log for debugging.
        const errorAddMessageFormatted = `NUACOM-LOG: [CONVERSATIONS] - Failed to add message to conversation ID ${targetId}. Reason: ${errorCodeAddingMessage} | Raw Error: ${JSON.stringify(
          err
        )} | Payload: ${JSON.stringify({
          channelType,
          templateId,
          templateParams: templateVariables,
          messageType: draft?.file?.type,
        })}`;

        log.error(errorAddMessageFormatted, {
          message: content,
        });
        electron.log(errorAddMessageFormatted);
      } finally {
        setLoading(false);
      }
    },
    [setErrorMessage, setLoading, dispatch]
  );

  return {
    errorMessage,
    loading,
    create,
    addMessage,
    setErrorMessage,
    submitted,
    conversationId,
    setSubmitted,
  };
}
